import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import axios from "axios";
import "../styles/MemberLogin.css";
import { useNavigate, useLocation } from "react-router-dom";

const EdOtpLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();     
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  
  // Get OTP expiry timestamp from localStorage
  const expiryTime = Number(localStorage.getItem("otpExpiry"));
  const remaining = Math.floor((expiryTime - Date.now()) / 1000);
  
  const [timer, setTimer] = useState(remaining > 0 ? remaining : 0);

  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const userEmail = location.state?.email || "";

  useEffect(() => {
    // Don't start timer if already expired
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup old interval before creating a new one
    return () => clearInterval(interval);
  }, [timer]);

  // 🔹 Update OTP digit
  const handleOtpChange = (value, index) => {
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to next field automatically
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    }
  };

  // 🔹 Combine OTP digits into single string
  const otpValue = otp.join("");

  // 🔹 Resend OTP function
  const handleResendOtp = async () => {
    if (timer > 0 && !isOtpExpired) return;
    
    setResendLoading(true);
    setErrorMessage("");
    
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_ROLE}/resend-otp`, {
        email: userEmail
      });
      
      if (res.data.success) {
        const expiryTimestamp = new Date(res.data.signin_otp_expiry).getTime();
        localStorage.setItem("otpExpiry", expiryTimestamp);

        const remaining = Math.max(
          Math.floor((expiryTimestamp - Date.now()) / 1000),
          0
        );
        setTimer(remaining);
        setIsOtpExpired(false);
        setOtp(["", "", "", "", "", ""]); // Clear OTP fields
        setErrorMessage("New OTP has been sent to your email.");
        // Focus on first OTP field
        document.getElementById(`otp-0`).focus();
      } else {
        setErrorMessage(res.data.message || "Failed to resend OTP.");
      }
    } catch (error) {
      console.error("Resend OTP failed:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to resend OTP. Please try again."
      );
      // If there's a retryAfter in response, set timer
      if (error.response?.data?.retryAfter) {
        setTimer(error.response.data.retryAfter);
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        // Clear current digit if it’s not empty
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // Move focus to previous input if current is already empty
        document.getElementById(`otp-${index - 1}`).focus();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    } else if (e.key === "Enter") {
      handleSignin(e); // ✅ optional: allow Enter to trigger submit
    }
  };

  // 🔹 Submit OTP verification request
  const handleSignin = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (isOtpExpired) {
      setErrorMessage("OTP has expired. Please request a new one.");
      return;
    }

    if (otpValue.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP.");
      return;
    }

    setLoading(true);
    try {
      const rolebasedUrl = process.env.REACT_APP_API_BASE_ROLE;
      console.log("Role-based URL:", rolebasedUrl);

      const response = await fetch(`${rolebasedUrl}/verify-login-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          otp: otpValue,
          email: userEmail,
        }),
      });

      const data = await response.json();
      console.log("OTP Verification Response:", data);

      if (!response.ok || !data.success) {
        setErrorMessage(data.message || "Invalid OTP, please try again.");
      } else {
        // ✅ Store token and user details
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.removeItem("otpExpiry");

        // ✅ Redirect only if user is EDCC
        if (data.user && data.user.rolename === "EDCC") {
          navigate("/", { state: { user: data.user, token: data.token } });
        } else {
          setErrorMessage("Access denied. Only EDCC users can log in here.");
        }
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      setErrorMessage(
        error.response?.data?.message || "Server error, please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="member-login-section">
        <Container>
          <div className="login-box">
            <Row>
              {/* Left Side */}
              <Col md={6} className="login-left mb-4 mb-md-0">
                <div className="login-form-container">
                  <h4 className="login-title">
                    EDCC Login <span className="dropdown-arrow">↓</span>
                  </h4>
                </div>
              </Col>

              {/* Right Side (OTP Form) */}
              <Col md={6}>
                {/* Timer Display */}
                {timer > 0 ? (
                  <div className="text-center mb-3">
                    <div className="timer-text">
                      OTP expires in: {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
                    </div>
                  </div>
                ) : (
                  <div className="text-center mb-3">
                    <div className="timer-text expired">
                      OTP has expired
                    </div>
                  </div>
                )}
                
                <Form onSubmit={(e) => handleSignin(e)}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label">Enter OTP</Form.Label>
                    <div className="d-flex gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <Form.Control
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          maxLength="1"
                          value={otp[index]}
                          onChange={(e) => handleOtpChange(e.target.value, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}  // ✅ added
                          className="edcc-otp-digit text-center"
                          disabled={isOtpExpired}
                        />
                      ))}
                    </div>
                  </Form.Group>

                  {errorMessage && (
                    <div className={`mb-2 ${errorMessage.includes("New OTP") ? "text-success" : "text-danger"}`}>
                      {errorMessage}
                    </div>
                  )}

                  <div className="mb-3">
                    <Button
                      variant="link"
                      className="custom-link p-0"
                      onClick={handleResendOtp}
                      disabled={(timer > 0 && !isOtpExpired) || resendLoading}
                    >
                      {resendLoading ? "Sending..." : "Resend OTP"}
                    </Button>
                  </div>

                  <div className="text-center mb-2">
                  <Button
                    className="custom-btn w-100"
                    type="submit"  // ✅ changed
                    disabled={loading || isOtpExpired}
                  >
                    {loading ? "Verifying..." : "SIGN IN"}
                  </Button>
                  </div>
                </Form>
              </Col>
            </Row>
          </div>
        </Container>
      </section>
    </>
  );
};

export default EdOtpLogin;